import {
    type CSSProperties,
    type FocusEventHandler,
    type MouseEvent as ReactMouseEvent,
    type ReactNode,
    useLayoutEffect,
} from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faCheckSquare,
    faChevronDown,
    faChevronUp,
    faCircleInfo,
    faSquare,
    faSquareMinus,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';

import { cn } from '../../lib/utils';
import {
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    Command as CommandPrimitive,
} from '../../shadcn/components/ui/command';
import type { CommandSelectItem, CommandSelectOption, CommandSelectOptionId } from '../CommandSelect';
import { defaultLocalFilter, getSelectableOptions, normalizeString } from '../CommandSelect/utils';
import { FieldDescription } from '../FieldDescription';
import { FieldError } from '../FieldError';
import { Tooltip } from '../Tooltip';

const getMultiSelectCheckboxIconClass = (selected: boolean, indeterminate = false): string =>
    clsx('h-4 w-4 shrink-0', {
        'text-blue-500': selected,
        'text-blue-400': indeterminate,
        'text-gray-400': !selected && !indeterminate,
    });

const getSelectAllCheckboxState = (
    allVisibleSelected: boolean,
    someVisibleSelected: boolean
): { icon: IconDefinition; iconClassName: string; ariaChecked: boolean | 'mixed' } => {
    if (allVisibleSelected) {
        return {
            icon: faCheckSquare,
            iconClassName: getMultiSelectCheckboxIconClass(true),
            ariaChecked: true,
        };
    }
    if (someVisibleSelected) {
        return {
            icon: faSquareMinus,
            iconClassName: getMultiSelectCheckboxIconClass(false, true),
            ariaChecked: 'mixed',
        };
    }
    return {
        icon: faSquare,
        iconClassName: getMultiSelectCheckboxIconClass(false),
        ariaChecked: false,
    };
};

const optionRowClassName = (isDisabled: boolean): string =>
    clsx(
        'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-gray-100',
        {
            'opacity-50 cursor-not-allowed': isDisabled,
        }
    );

const DROPDOWN_MIN_WIDTH = 288;

export interface CommandMultiSelectProps {
    items: CommandSelectItem[];
    mode?: 'multiple' | 'single';
    size?: 'sm' | 'md';
    value?: CommandSelectOptionId[];
    defaultValue?: CommandSelectOptionId[];
    onChange?: (value: CommandSelectOptionId[], options: CommandSelectOption[]) => void;
    label?: ReactNode;
    tooltip?: ReactNode;
    description?: ReactNode;
    placeholder?: string;
    dataTestid?: string;
    className?: string;
    inputClassName?: string;
    disabled?: boolean;
    required?: boolean;
    errorMessage?: string;
    nonRemovableValues?: CommandSelectOptionId[];
    emptyText?: string;
    onCreate?: (label: string) => Promise<CommandSelectOption | null> | CommandSelectOption | null;
    onSearchChange?: (query: string) => void;
    showCreateWhenNoMatch?: boolean;
    maxDropdownHeight?: number;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    layout?: 'horizontal' | 'vertical';
    showSelectAll?: boolean;
    singleLine?: boolean;
    maxSelected?: number;
    /**
     * Groupes d'options : chaque groupe ajoute/retire en un clic l'ensemble de ses `memberIds`.
     * Les ids restent stockés à plat dans `value` (le groupe lui-même n'est pas une valeur).
     */
    groups?: CommandMultiSelectGroup[];
}

export interface CommandMultiSelectGroup {
    id: string;
    label: string;
    memberIds: CommandSelectOptionId[];
}

export function CommandMultiSelect({
    items = [],
    mode = 'multiple',
    size = 'md',
    value: valueProp,
    defaultValue = [],
    onChange,
    label,
    tooltip,
    description,
    placeholder,
    dataTestid,
    className,
    inputClassName,
    disabled = false,
    required = false,
    errorMessage,
    nonRemovableValues = [],
    emptyText = 'Aucun résultat',
    onCreate,
    onSearchChange,
    showCreateWhenNoMatch = true,
    maxDropdownHeight = 240,
    onBlur,
    layout = 'horizontal',
    showSelectAll = false,
    singleLine = false,
    maxSelected,
    groups = [],
}: CommandMultiSelectProps) {
    const isVerticalLayout = layout === 'vertical';
    const isControlled = valueProp !== undefined;
    const normalizedDefaultValue = mode === 'single' ? defaultValue.slice(0, 1) : defaultValue;
    const [uncontrolledValue, setUncontrolledValue] = useState<CommandSelectOptionId[]>(normalizedDefaultValue);
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [createdOptions, setCreatedOptions] = useState<CommandSelectOption[]>([]);
    const [selectedOptionsCache, setSelectedOptionsCache] = useState<Map<CommandSelectOptionId, CommandSelectOption>>(
        new Map()
    );
    const { t } = useTranslation();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);
    const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>();

    const rawValue = isControlled ? (valueProp ?? []) : uncontrolledValue;
    const value = mode === 'single' ? rawValue.slice(0, 1) : rawValue;

    const prevItemsRef = useRef<CommandSelectItem[]>(items);
    const prevValueRef = useRef<CommandSelectOptionId[]>(value ?? []);

    const allOptions = useMemo(() => {
        const fromItems = items.filter((i): i is CommandSelectOption => 'id' in i && 'label' in i);
        const byId = new Map<CommandSelectOptionId, CommandSelectOption>();
        for (const option of selectedOptionsCache.values()) {
            byId.set(option.id, option);
        }
        for (const option of fromItems) {
            if (!byId.has(option.id)) {
                byId.set(option.id, option);
            }
        }
        for (const option of createdOptions) {
            if (!byId.has(option.id)) {
                byId.set(option.id, option);
            }
        }
        return byId;
    }, [items, createdOptions, selectedOptionsCache]);

    const selectedOptions: CommandSelectOption[] = useMemo(
        () =>
            Array.isArray(value)
                ? value
                      .map((id) => {
                          const fromCache = selectedOptionsCache.get(id);
                          if (fromCache) return fromCache;
                          return allOptions.get(id);
                      })
                      .filter((opt): opt is CommandSelectOption => Boolean(opt))
                : [],
        [value, allOptions, selectedOptionsCache]
    );

    const filteredItems = useMemo(() => {
        const itemsIds = new Set(
            items.filter((i): i is CommandSelectOption => 'id' in i && 'label' in i).map((i) => i.id)
        );
        const uniqueCreatedOptions = createdOptions.filter((opt) => !itemsIds.has(opt.id));
        const itemsWithCreated = [...items, ...uniqueCreatedOptions];

        const dedupedById: CommandSelectItem[] = [];
        const seenIds = new Set<CommandSelectOptionId>();
        for (const item of itemsWithCreated) {
            if ('id' in item && 'label' in item) {
                if (seenIds.has(item.id)) continue;
                seenIds.add(item.id);
                dedupedById.push(item);
            } else {
                dedupedById.push(item);
            }
        }

        if (onSearchChange) {
            return dedupedById;
        }
        return defaultLocalFilter(query, dedupedById);
    }, [items, query, onSearchChange, createdOptions]);

    const selectableOptions = useMemo(() => getSelectableOptions(filteredItems), [filteredItems]);

    const queryMatchesAnyOption = useMemo(() => {
        if (!query.trim()) return false;
        const normalizedQuery = normalizeString(query.trim());
        return selectableOptions.some((option) => normalizeString(option.label).includes(normalizedQuery));
    }, [query, selectableOptions]);

    const showCreate = !!onCreate && showCreateWhenNoMatch && !queryMatchesAnyOption && query.trim().length > 0;

    const isValueSelected = (id: CommandSelectOptionId) => Array.isArray(value) && value.includes(id);

    const atSelectionLimit =
        mode === 'multiple' && maxSelected !== undefined && Array.isArray(value) && value.length >= maxSelected;

    const allVisibleSelected =
        selectableOptions.length > 0 && selectableOptions.every((option) => isValueSelected(option.id));
    const selectedVisibleCount = selectableOptions.filter((option) => isValueSelected(option.id)).length;
    const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;
    const selectAllCheckboxState = getSelectAllCheckboxState(allVisibleSelected, someVisibleSelected);

    const isNonRemovable = (id: CommandSelectOptionId) => nonRemovableValues.includes(id);

    const updateValue = (
        nextIds: CommandSelectOptionId[],
        providedOptions?: Map<CommandSelectOptionId, CommandSelectOption>
    ) => {
        const normalizedNextIds = mode === 'single' ? nextIds.slice(0, 1) : nextIds;
        if (!isControlled) {
            setUncontrolledValue(normalizedNextIds);
        }
        const optionsSource = providedOptions ?? allOptions;
        const nextOptions = normalizedNextIds
            .map((id) => optionsSource.get(id))
            .filter((opt): opt is CommandSelectOption => Boolean(opt));
        onChange?.(normalizedNextIds, nextOptions);
    };

    const handleToggleAll = () => {
        if (disabled || mode === 'single' || selectableOptions.length === 0) return;

        const visibleIds = new Set(selectableOptions.map((option) => option.id));

        if (allVisibleSelected) {
            const next = value.filter((id) => isNonRemovable(id) || !visibleIds.has(id));
            updateValue(next);
            return;
        }

        const updatedCache = new Map(selectedOptionsCache);
        for (const option of selectableOptions) {
            updatedCache.set(option.id, option);
        }
        setSelectedOptionsCache(updatedCache);

        const mergedIds = [...new Set([...value, ...selectableOptions.map((option) => option.id)])];
        const nextIds = maxSelected !== undefined ? mergedIds.slice(0, maxSelected) : mergedIds;
        const cacheForOptions = new Map(updatedCache);
        for (const id of nextIds) {
            if (!cacheForOptions.has(id)) {
                const fromAllOptions = allOptions.get(id);
                if (fromAllOptions) {
                    cacheForOptions.set(id, fromAllOptions);
                }
            }
        }
        updateValue(nextIds, cacheForOptions);
    };

    const getGroupSelectionState = (group: CommandMultiSelectGroup) => {
        const total = group.memberIds.length;
        const selectedCount = group.memberIds.filter((id) => isValueSelected(id)).length;
        return {
            allSelected: total > 0 && selectedCount === total,
            someSelected: selectedCount > 0 && selectedCount < total,
        };
    };

    const toggleGroup = (group: CommandMultiSelectGroup) => {
        if (disabled || mode === 'single' || group.memberIds.length === 0) return;

        const { allSelected } = getGroupSelectionState(group);
        const memberIdsSet = new Set(group.memberIds);

        if (allSelected) {
            const next = value.filter((id) => isNonRemovable(id) || !memberIdsSet.has(id));
            updateValue(next);
            return;
        }

        const updatedCache = new Map(selectedOptionsCache);
        for (const id of group.memberIds) {
            if (!updatedCache.has(id)) {
                const fromAllOptions = allOptions.get(id);
                if (fromAllOptions) {
                    updatedCache.set(id, fromAllOptions);
                }
            }
        }
        setSelectedOptionsCache(updatedCache);

        const mergedIds = [...new Set([...value, ...group.memberIds])];
        const nextIds = maxSelected !== undefined ? mergedIds.slice(0, maxSelected) : mergedIds;
        const cacheForOptions = new Map(updatedCache);
        for (const id of nextIds) {
            if (!cacheForOptions.has(id)) {
                const fromAllOptions = allOptions.get(id);
                if (fromAllOptions) {
                    cacheForOptions.set(id, fromAllOptions);
                }
            }
        }
        updateValue(nextIds, cacheForOptions);
    };

    const toggleOption = (option: CommandSelectOption) => {
        if (disabled) return;
        if (option.disabled) return;

        const alreadySelected = isValueSelected(option.id);
        if (alreadySelected && isNonRemovable(option.id)) {
            return;
        }
        if (!alreadySelected && atSelectionLimit) {
            return;
        }

        const updatedCache = new Map(selectedOptionsCache);
        updatedCache.set(option.id, option);
        setSelectedOptionsCache(updatedCache);

        if (alreadySelected) {
            const next = value.filter((id) => id !== option.id);
            const cacheForOptions = new Map(updatedCache);
            for (const id of next) {
                if (!cacheForOptions.has(id)) {
                    const fromAllOptions = allOptions.get(id);
                    if (fromAllOptions) {
                        cacheForOptions.set(id, fromAllOptions);
                    }
                }
            }
            updateValue(next, cacheForOptions);
        } else {
            if (mode === 'single') {
                const [currentlySelected] = value;
                if (currentlySelected && isNonRemovable(currentlySelected)) {
                    return;
                }
                updateValue([option.id], updatedCache);
                setOpen(false);
                setQuery('');
            } else {
                const next = [...(value || []), option.id];
                const cacheForOptions = new Map(updatedCache);
                for (const id of next) {
                    if (!cacheForOptions.has(id)) {
                        const fromAllOptions = allOptions.get(id);
                        if (fromAllOptions) {
                            cacheForOptions.set(id, fromAllOptions);
                        }
                    }
                }
                updateValue(next, cacheForOptions);
            }
        }
    };

    const handleContainerClick = (event: ReactMouseEvent<HTMLDivElement>) => {
        if (disabled) return;

        const target = event.target as Node | null;
        if (searchInputRef.current && target && searchInputRef.current.contains(target)) {
            return;
        }

        setOpen((prev) => {
            const next = !prev;
            if (next) {
                setTimeout(() => {
                    searchInputRef.current?.focus();
                }, 0);
            }
            return next;
        });
    };

    const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
        onBlur?.(event);
    };

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (!containerRef.current || !dropdownRef.current) return;
            const target = event.target as HTMLElement | null;
            if (!target) return;

            const isCommandItem = target.closest('[role="option"]');
            if (isCommandItem) return;

            if (!containerRef.current.contains(target) && !dropdownRef.current.contains(target)) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open]);

    useEffect(() => {
        const currentValue = value ?? [];
        const valueChanged =
            prevValueRef.current.length !== currentValue.length ||
            !prevValueRef.current.every((id, idx) => currentValue[idx] === id);

        const itemsIds = items
            .filter((i): i is CommandSelectOption => 'id' in i && 'label' in i)
            .map((i) => i.id)
            .sort()
            .join(',');
        const prevItemsIds = prevItemsRef.current
            .filter((i): i is CommandSelectOption => 'id' in i && 'label' in i)
            .map((i) => i.id)
            .sort()
            .join(',');
        const itemsChanged = itemsIds !== prevItemsIds;

        if (!valueChanged && !itemsChanged) {
            return;
        }

        prevValueRef.current = currentValue;
        prevItemsRef.current = items;

        setSelectedOptionsCache((prev) => {
            const next = new Map(prev);

            if (Array.isArray(currentValue) && currentValue.length > 0) {
                for (const id of currentValue) {
                    if (prev.has(id)) {
                        next.set(id, prev.get(id)!);
                    }
                }
                for (const id of currentValue) {
                    if (next.has(id)) continue;
                    const fromItems = items.find(
                        (i): i is CommandSelectOption => 'id' in i && 'label' in i && i.id === id
                    );
                    if (fromItems) {
                        next.set(id, fromItems);
                    }
                }
                for (const id of next.keys()) {
                    if (!currentValue.includes(id)) {
                        const stillInItems = items.some(
                            (i): i is CommandSelectOption => 'id' in i && 'label' in i && i.id === id
                        );
                        if (!stillInItems) {
                            next.delete(id);
                        }
                    }
                }
            } else {
                for (const id of next.keys()) {
                    const stillInItems = items.some(
                        (i): i is CommandSelectOption => 'id' in i && 'label' in i && i.id === id
                    );
                    if (!stillInItems) {
                        next.delete(id);
                    }
                }
            }

            if (next.size !== prev.size) {
                return next;
            }
            for (const [id, option] of next) {
                const prevOption = prev.get(id);
                if (!prevOption || prevOption.label !== option.label) {
                    return next;
                }
            }
            return prev;
        });
    }, [value, items]);

    useLayoutEffect(() => {
        if (!open) return;

        const updateDropdownPosition = () => {
            const el = containerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const margin = 4;
            setDropdownStyle({
                position: 'fixed',
                left: rect.left,
                top: rect.bottom + margin,
                width: rect.width,
                minWidth: DROPDOWN_MIN_WIDTH,
            });
        };

        updateDropdownPosition();
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);
        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
        };
    }, [open]);

    const handleCreateClick = async () => {
        if (!onCreate) return;
        const trimmed = query.trim();
        if (!trimmed) return;

        const created = await Promise.resolve(onCreate(trimmed));
        if (created) {
            setCreatedOptions((prev) => [...prev, created]);
            const updatedCache = new Map(selectedOptionsCache);
            updatedCache.set(created.id, created);
            setSelectedOptionsCache(updatedCache);

            const cacheForOptions = new Map(updatedCache);
            const currentValue = value || [];
            for (const id of currentValue) {
                if (!cacheForOptions.has(id)) {
                    const fromAllOptions = allOptions.get(id);
                    if (fromAllOptions) {
                        cacheForOptions.set(id, fromAllOptions);
                    }
                }
            }
            cacheForOptions.set(created.id, created);

            if (mode === 'single') {
                const [currentlySelected] = value;
                if (currentlySelected && isNonRemovable(currentlySelected)) {
                    return;
                }
                updateValue([created.id], cacheForOptions);
                setOpen(false);
                setQuery('');
            } else {
                const next = [...(value || []), created.id];
                updateValue(next, cacheForOptions);
                setQuery('');
            }
        }
    };

    return (
        <div
            className={cn('w-full min-w-0', className)}
            data-component-name='CommandMultiSelect'
        >
            {label && (
                <div className='mb-2 flex items-center gap-1.5'>
                    <label
                        className={clsx(
                            'block truncate text-xs font-medium leading-4',
                            disabled ? 'text-gray-400' : 'text-gray-800'
                        )}
                        aria-disabled={disabled}
                        data-testid={dataTestid && `${dataTestid}-label`}
                    >
                        {label} {required && '*'}
                    </label>
                    {tooltip && (
                        <Tooltip content={tooltip}>
                            <FontAwesomeIcon
                                icon={faCircleInfo}
                                className='size-3 text-gray-500'
                                data-testid={dataTestid && `${dataTestid}-tooltip-icon`}
                            />
                        </Tooltip>
                    )}
                </div>
            )}

            <div
                ref={containerRef}
                className={cn(
                    'relative flex ring-1 ring-inset px-2 focus-within:ring-2 focus-within:ring-inset cursor-pointer',
                    size === 'sm' ? 'rounded-lg py-1 min-h-8' : 'rounded-md py-1 min-h-[38px]',
                    disabled ? 'bg-gray-100' : 'bg-white',
                    errorMessage
                        ? 'ring-red-300 focus-within:ring-red-300'
                        : 'ring-gray-300 focus-within:ring-blue-300',
                    open ? 'overflow-visible' : 'overflow-hidden'
                )}
                onClick={handleContainerClick}
                data-testid={dataTestid}
            >
                <div
                    className={clsx(
                        'flex min-w-0 flex-1 gap-2',
                        isVerticalLayout
                            ? 'flex-col items-start'
                            : singleLine
                              ? 'flex-nowrap items-center overflow-hidden'
                              : 'flex-wrap items-center',
                        'focus-within:max-h-60 focus-within:overflow-y-auto'
                    )}
                >
                    {(() => {
                        const maxVisibleCollapsed = 3;
                        const isExpanded = open;
                        const visibleOptions = isExpanded
                            ? selectedOptions
                            : selectedOptions.slice(0, maxVisibleCollapsed);
                        const hiddenCount = isExpanded ? 0 : selectedOptions.length - visibleOptions.length;

                        const tags = visibleOptions.map((option) => {
                            const locked = isNonRemovable(option.id);
                            const canRemove = !disabled && !locked;
                            return (
                                <span
                                    key={String(option.id)}
                                    className={clsx(
                                        'inline-flex items-center gap-2 rounded-full ',
                                        disabled || !canRemove ? 'text-gray-400' : 'text-gray-700',
                                        mode === 'single'
                                            ? 'border-none p-0 text-sm'
                                            : clsx(
                                                  'border text-xs border-gray-300',
                                                  size === 'sm' ? 'px-2 py-0.5' : 'px-3 py-1'
                                              )
                                    )}
                                    data-testid={dataTestid && `${dataTestid}-tag-${String(option.id)}`}
                                >
                                    {option.icon && (
                                        <span className='flex-shrink-0 flex items-center justify-center'>
                                            {option.icon}
                                        </span>
                                    )}
                                    <span
                                        className={clsx('truncate max-w-[14rem]', {
                                            'max-w-[10rem]': selectedOptions.length > maxVisibleCollapsed,
                                            'max-w-full': mode === 'single',
                                        })}
                                        title={option.label}
                                    >
                                        {option.label}
                                    </span>
                                    {canRemove && mode !== 'single' && (
                                        <button
                                            type='button'
                                            aria-label={`Remove ${option.label}`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                toggleOption(option);
                                            }}
                                            className='leading-none'
                                            disabled={disabled}
                                        >
                                            ×
                                        </button>
                                    )}
                                </span>
                            );
                        });

                        if (!isExpanded && hiddenCount > 0) {
                            tags.push(
                                <span
                                    key='command-multi-select-overflow'
                                    className={clsx(
                                        'inline-flex items-center gap-2 rounded-full border text-xs',
                                        size === 'sm' ? 'px-2 py-0.5' : 'px-3 py-1',
                                        'border-gray-300 text-gray-700 bg-gray-50'
                                    )}
                                >
                                    +{hiddenCount}
                                </span>
                            );
                        }

                        if (tags.length === 0 && placeholder) {
                            tags.push(
                                <span
                                    key='placeholder'
                                    className='truncate text-sm text-gray-500'
                                >
                                    {placeholder}
                                </span>
                            );
                        }

                        return tags;
                    })()}
                </div>

                <span className={clsx('ml-2 mr-1.5 flex items-center', disabled ? 'text-gray-300' : 'text-gray-400')}>
                    <FontAwesomeIcon
                        icon={open ? faChevronUp : faChevronDown}
                        className={size === 'sm' ? 'text-xs h-2.5 w-2.5' : 'h-3 w-3'}
                    />
                </span>

                {open &&
                    createPortal(
                        <div
                            ref={dropdownRef}
                            style={dropdownStyle}
                            className='mt-1 rounded-lg border border-gray-200 bg-white shadow-lg z-modal'
                        >
                            <CommandPrimitive
                                className='max-h-full'
                                shouldFilter={false}
                            >
                                <CommandInput
                                    ref={searchInputRef}
                                    value={query}
                                    onValueChange={(val) => {
                                        setQuery(val);
                                        onSearchChange?.(val);
                                    }}
                                    placeholder={placeholder}
                                    onBlur={handleBlur}
                                    className={clsx('focus:ring-0 focus:border-gray-300', inputClassName)}
                                />
                                {showSelectAll && mode === 'multiple' && selectableOptions.length > 0 && (
                                    <div className='shrink-0 border-b border-gray-200 bg-gray-50 px-1 py-0.5'>
                                        <button
                                            type='button'
                                            role='option'
                                            aria-checked={selectAllCheckboxState.ariaChecked}
                                            className={optionRowClassName(disabled)}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleToggleAll();
                                            }}
                                            disabled={disabled}
                                            data-testid={dataTestid && `${dataTestid}-select-all`}
                                        >
                                            <FontAwesomeIcon
                                                icon={selectAllCheckboxState.icon}
                                                className={selectAllCheckboxState.iconClassName}
                                            />
                                            <span className='flex-1 truncate text-left font-medium text-gray-800'>
                                                {allVisibleSelected ? t('Unselect all') : t('Select all')}
                                            </span>
                                            <span className='shrink-0 tabular-nums text-xs text-gray-500'>
                                                {selectedVisibleCount}/{selectableOptions.length}
                                            </span>
                                        </button>
                                    </div>
                                )}
                                {groups.length > 0 && mode === 'multiple' && !query.trim() && (
                                    <div className='shrink-0 border-b border-gray-200 bg-gray-50 px-1 py-0.5'>
                                        {groups.map((group) => {
                                            const { allSelected, someSelected } = getGroupSelectionState(group);
                                            const checkboxState = getSelectAllCheckboxState(allSelected, someSelected);
                                            return (
                                                <button
                                                    key={group.id}
                                                    type='button'
                                                    role='option'
                                                    aria-checked={checkboxState.ariaChecked}
                                                    className={optionRowClassName(disabled)}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        toggleGroup(group);
                                                    }}
                                                    disabled={disabled}
                                                    data-testid={dataTestid && `${dataTestid}-group-${group.id}`}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={checkboxState.icon}
                                                        className={checkboxState.iconClassName}
                                                    />
                                                    <span className='flex-1 truncate text-left font-medium text-gray-800'>
                                                        {group.label}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                <CommandList
                                    style={{ maxHeight: `${maxDropdownHeight}px` }}
                                    className='max-h-full overflow-y-auto'
                                >
                                    {selectableOptions.length === 0 && !showCreate ? (
                                        <CommandEmpty>{emptyText}</CommandEmpty>
                                    ) : (
                                        <>
                                            <CommandGroup>
                                                {filteredItems.map((item, index) => {
                                                    if (!('id' in item && 'label' in item)) {
                                                        return null;
                                                    }
                                                    const itemOption = item as CommandSelectOption;
                                                    const option =
                                                        selectedOptionsCache.get(itemOption.id) ?? itemOption;
                                                    const selected = isValueSelected(option.id);
                                                    const locked = isNonRemovable(option.id);
                                                    const isDisabled =
                                                        disabled ||
                                                        option.disabled ||
                                                        (selected && locked) ||
                                                        (!selected && atSelectionLimit);

                                                    const handleItemClick = (e: React.MouseEvent) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        if (isDisabled) return;
                                                        toggleOption(option);
                                                    };

                                                    const handleMouseDown = (e: React.MouseEvent) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    };

                                                    return (
                                                        <div
                                                            key={`${String(option.id)}-${index}`}
                                                            role='option'
                                                            aria-selected={selected}
                                                            onMouseDown={handleMouseDown}
                                                            onClick={handleItemClick}
                                                            className={optionRowClassName(isDisabled)}
                                                        >
                                                            {mode === 'multiple' && (
                                                                <FontAwesomeIcon
                                                                    icon={selected ? faCheckSquare : faSquare}
                                                                    className={getMultiSelectCheckboxIconClass(
                                                                        selected
                                                                    )}
                                                                />
                                                            )}
                                                            {option.icon && (
                                                                <span className='flex-shrink-0 flex items-center justify-center'>
                                                                    {option.icon}
                                                                </span>
                                                            )}
                                                            <span className='flex-1 truncate'>{option.label}</span>
                                                            {mode === 'single' && selected && (
                                                                <FontAwesomeIcon
                                                                    icon={faCheckSquare}
                                                                    className='h-4 w-4 shrink-0 text-blue-500'
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </CommandGroup>

                                            {showCreate && (
                                                <CommandItem
                                                    value='__create__'
                                                    onSelect={() => {
                                                        void handleCreateClick();
                                                    }}
                                                    className={clsx('cursor-pointer', {
                                                        'opacity-50 cursor-not-allowed': disabled || !query.trim(),
                                                    })}
                                                >
                                                    <span className='flex-1 truncate'>
                                                        {t('Create “{{query}}”', {
                                                            query: query.trim() || placeholder,
                                                        })}
                                                    </span>
                                                </CommandItem>
                                            )}
                                        </>
                                    )}
                                </CommandList>
                            </CommandPrimitive>
                        </div>,
                        document.body
                    )}
            </div>

            {description && (
                <FieldDescription dataTestid={dataTestid && `${dataTestid}-description`}>
                    {description}
                </FieldDescription>
            )}
            {errorMessage && <FieldError dataTestid={dataTestid && `${dataTestid}-error`}>{errorMessage}</FieldError>}
        </div>
    );
}
